import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponseBase, HttpEventType, HttpEvent } from '@angular/common/http';

import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { AuthorizeService, TokenAuthResponseModel } from './authorize.service';

@Injectable()
export class BackendService {

  private isRefreshing = false;
  private refreshPromise: Promise<void | TokenAuthResponseModel>;

  constructor(private http: HttpClient,
    private authService: AuthorizeService) { }


  public getAuthorizationHeader(): string {
    return 'Bearer ' + this.authService.accessToken;
  }

  public getHttpHeaders(headers?: HttpHeaders, isAuthRequired?: boolean): HttpHeaders {

    if (headers === undefined) { headers = this.getStandardHeaders(); }

    if (isAuthRequired === undefined) { isAuthRequired = true; }
    if (isAuthRequired) { headers = headers.set('authorization', this.getAuthorizationHeader()); }

    return headers;
  }

  public getStandardHeaders(): HttpHeaders {
    return new HttpHeaders({
      'content-type': 'application/json',
      'cache-control': 'no-cache',
      'pragma': 'no-cache'
    });

  }

  public request(verb: string, url: string, body?: any, headers?: HttpHeaders, isAuthRequired?: boolean): Observable<object> {

    let backend$ = new Subject<object>();
    this.executeRequest(backend$, verb, url, body, headers, isAuthRequired);
    return backend$.asObservable();
  }

  public executeRequest(backend: Subject<object>, verb: string, url: string, body: any, headers: HttpHeaders, isAuthRequired: boolean) {
    let httpHeaders = this.getHttpHeaders(headers, isAuthRequired);
    let httpRequest = new HttpRequest(verb, url, body, { headers: httpHeaders, reportProgress: false });

    this.http.request(httpRequest).subscribe((event: HttpEvent<any>) => {

      if (event.type !== HttpEventType.Response) { return; }

      backend.next(event.body);
      backend.complete();

    }, (e) => {

      if (!(e instanceof HttpResponseBase)) {
        backend.error(e);
        backend.complete();
        return;
      }

      let response = e as HttpResponseBase;

      if (response.status !== 401) {
        backend.error(e);
        backend.complete();
        return;
      }


      if (!this.isRefreshing) {
        this.isRefreshing = true;

        this.refreshPromise = this.authService.refresh().toPromise()
          .then((model: TokenAuthResponseModel) => {

            this.isRefreshing = false;
            return model;

          }, (err) => {

            this.isRefreshing = false;
            return Promise.reject(err);

          });

      }

      this.refreshPromise = this.refreshPromise.then((model: TokenAuthResponseModel) => {

        if (model.code === 200) {
          this.executeRequest(backend, verb, url, body, headers, isAuthRequired);
          return model;
        }
        else {

          backend.error(model.message);
          backend.complete();

          throw model;
        }

      }, (err) => {

        backend.error(err);
        backend.complete();

        return Promise.reject(err);

      });
    });
  }

  public get(url: string, headers?: HttpHeaders, isAuthRequired?: boolean): Observable<object> {
    return this.request('GET', url, null, headers, isAuthRequired);
  }

  public post(url: string, body?: any, headers?: HttpHeaders, isAuthRequired?: boolean): Observable<object> {
    return this.request('POST', url, body, headers, isAuthRequired);
  }

  public put(url: string, body?: any, headers?: HttpHeaders, isAuthRequired?: boolean): Observable<object> {
    return this.request('PUT', url, body, headers, isAuthRequired);
  }

  public delete(url: string, headers?: HttpHeaders, isAuthRequired?: boolean): Observable<object> {
    return this.request('DELETE', url, null, headers, isAuthRequired);
  }
}
